// combatpro/panels/spells/SpellExec.js
//
// Combat PRO - SpellExec (Stage 48B-1: Spell Execution UI).
//
// Stage 45A left this as the empty per-slot execution mount
// (spellExec${spellIdx}-${index}). Stage 48B-1 populates it with the Execution
// Preview panel and the two cast buttons, reproducing the legacy
// vi_renderSpellExecPreview (battletracker.html 11769-11826) WITHOUT recreating
// any runtime logic:
//   - The spell is resolved with getSpellData (injected window.VI_SPELLS_DB,
//     external, never embedded) and prepared with prepareSpellExecution.
//   - The preview cells are driven by buildSpellPreview (runtime output) - Damage,
//     Save (the runtime composes Save Effect into this string), Duration (the
//     runtime composes Concentration into this string), Condition, and Target
//     Type. School and Range are raw passthrough from the prepared spell, since
//     buildSpellPreview does not expose them; nothing is recomputed.
//   - The two buttons invoke the existing castSpell runtime ONLY:
//       Cast On Active Monster      -> castSpell(index, spellIdx, [currentCombatantIndex])
//       Cast On All Active Monsters -> castSpell(index, spellIdx, all active non-caster)
//     "all active non-caster" matches the legacy vi_executeSpellAll filter
//     (i !== caster, currentHP > 0). No manual selection, no highlighting, no
//     pills, no players, no auto/AI targeting (deferred to 48B-2).
//
// Stage 120 (Cast-Flow Convenience Wrappers): migrate the remaining legacy
// cast-flow quick-action wrappers as thin, view-local orchestration handlers that
// reuse the already-migrated runtimes only. This file is the host because it
// already owns the spell cast UI and the castSpell seam.
//   - vi_castAndEndTurn  (battletracker.html 12614) -> onCastAndEndTurn:
//       castSpell(...) then nextCombatant() exactly once, only on strict === true.
//   - vi_castAndNextRound (battletracker.html 12620) -> onCastAndNextRound:
//       castSpell(...) then nextRound() exactly once, only on strict === true.
//     Both reuse the existing castSpell / nextCombatant / nextRound context
//     runtimes (no new cast, validation, initiative or round pipeline), and their
//     target resolution is identical to onCastActive so cast behaviour, combat-log
//     entries and last-action parity are byte-identical to the single-cast button.
//     The legacy strict guard "if (result !== true) return;" (12616/12622) is
//     preserved, so a failed cast advances neither turn nor round.
//   - vi_showCastFeedback (battletracker.html 12584): INTENTIONALLY NOT MIGRATED.
//     It is a DOM toast with ZERO call sites anywhere in the legacy file (dead
//     code, same category as the dead AI functions vi_logAISpellRecommendation /
//     vi_runAISpellTestSuite).
//   - vi_validateCast (battletracker.html 12592): INTENTIONALLY NOT MIGRATED. Its
//     return value is exactly targets.length === 0 -> false; every other line is
//     imperative DOM error/highlight feedback. The "no targets" contract is already
//     SATISFIED BY CONSTRUCTION here (onCastActive / the quick-action handlers fall
//     back to the active monster, so the target list is never empty and castSpell
//     is never reached with an empty list), and the "No targets selected" pill state
//     renders the declarative equivalent of the legacy error/highlight feedback.
//
// Reuses runtime only (no duplicated damage / condition / preview calculations).
// No optional chaining. No em dash. Do not edit battletracker.html.

import React, { useContext } from 'react';
import { CombatContext } from '../../contexts/CombatContext';
import { getSpellData } from '../../spell/spellEngine';
import { prepareSpellExecution, buildSpellPreview } from '../../spell/spellCastEngine';
import { autoSelectTargets } from '../../spell/spellAiEngine';
import SpellTargetSelector, { effectiveSpellTargets } from './SpellTargetSelector';

function SpellExec(props) {
  const index = props.index;
  const spellIdx = props.spellIdx;
  const { state, castSpell, spellTargetKey, setManualMode, nextCombatant, nextRound } = useContext(CombatContext);

  const mountId = 'spellExec' + spellIdx + '-' + index;
  const monster = state.monsters[index];
  const slot = (monster && monster.spells) ? monster.spells[spellIdx] : null;
  const spellName = slot ? slot.name : '';
  if (!spellName) { return <div id={mountId}></div>; }

  const spellsDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
  const rawSpell = getSpellData(spellName, spellsDb);
  if (!rawSpell) { return <div id={mountId}></div>; }
  const spell = prepareSpellExecution(rawSpell, monster) || rawSpell;

  // Preview targets = all active non-caster monsters (matches the legacy preview
  // target set 11780 and the Execute-on-All filter 11856).
  const targets = state.monsters.map((m, i) => i).filter(i => i !== index && (state.monsters[i].currentHP || 0) > 0);
  const preview = buildSpellPreview(spell, targets);
  if (!preview) { return <div id={mountId}></div>; }

  const typeClass = 'vi-exec-type-' + preview.spellType;
  const typeLabel = preview.spellType.charAt(0).toUpperCase() + preview.spellType.slice(1);

  const targetKey = spellTargetKey(index, spellIdx);
  const storedTargets = (state.spellTargets && state.spellTargets[targetKey]) ? state.spellTargets[targetKey] : [];
  const autoTargets = autoSelectTargets(spell, index, state.monsters, state.players) || [];
  const effectiveTargets = effectiveSpellTargets(state, targetKey, autoTargets);

  // Stage 48B-2: "Cast On Active Monster" now casts on the manually selected set
  // when any targets are stored for this slot, falling back to the active monster
  // (the Stage 48B-1 behaviour) when none are selected. Because the fallback always
  // supplies the active monster, the target list is never empty, so the legacy
  // vi_validateCast "no targets" block (12600) is satisfied by construction. After
  // casting, manual mode is cleared (legacy vi_executeSpellOnTargets 12194) while
  // the stored targets persist (the legacy store is not cleared). castSpell is the
  // only execution path - no targeting or runtime logic is duplicated here.
  function onCastActive() {
    const targetsToUse = effectiveTargets.length > 0 ? effectiveTargets : [state.currentCombatantIndex];
    castSpell(index, spellIdx, targetsToUse);
    setManualMode(null);
  }

  // Stage 120 (Cast-Flow Convenience Wrappers): thin orchestration handlers for the
  // legacy quick-action buttons vi_castAndEndTurn / vi_castAndNextRound
  // (battletracker.html 12614-12623). Each casts ONCE through the existing castSpell
  // runtime, then advances the initiative or round runtime EXACTLY once, and only on
  // a strict castSpell(...) === true - faithful to the legacy "if (result !== true)
  // return;" guard (12616/12622), so a failed cast advances neither turn nor round.
  // Target resolution is identical to onCastActive (stored targets, else the active
  // monster), so cast behaviour / combat-log / last-action parity is byte-identical
  // to the single-cast button and no cast, validation, initiative or round logic is
  // duplicated. D2: setManualMode(null) runs on the SUCCESS path only (after the
  // guard passes), matching the legacy internal manual-mode clear that a failed
  // vi_executeSpellOnTargets returns before reaching (12194).
  function onCastAndEndTurn() {
    const targetsToUse = storedTargets.length > 0 ? storedTargets : [state.currentCombatantIndex];
    if (castSpell(index, spellIdx, targetsToUse) !== true) { return; }
    setManualMode(null);
    nextCombatant();
  }

  function onCastAndNextRound() {
    const targetsToUse = storedTargets.length > 0 ? storedTargets : [state.currentCombatantIndex];
    if (castSpell(index, spellIdx, targetsToUse) !== true) { return; }
    setManualMode(null);
    nextRound();
  }

  function cell(label, value, valueClass) {
    if (value === null || value === undefined || value === '') { return null; }
    const cls = valueClass ? ('vi-spell-exec-value ' + valueClass) : 'vi-spell-exec-value';
    return (
      <div className="vi-spell-exec-cell">
        <div className="vi-spell-exec-label">{label}</div>
        <div className={cls}>{value}</div>
      </div>
    );
  }

  return (
    <div id={mountId}>
      <div className="vi-spell-exec-preview">
        <div className="vi-spell-exec-header">
          <span className="vi-spell-exec-title">Execution Preview</span>
          <span className={'vi-spell-exec-type ' + typeClass}>{typeLabel}</span>
        </div>
        <div className="vi-spell-exec-grid">
          {cell('Damage', preview.damage, 'dmg')}
          {cell('Save', preview.save, 'save')}
          {cell('Duration', preview.duration, 'dur')}
          {cell('Condition', preview.conditions, 'cond')}
          {cell('Target Type', preview.targeting, null)}
          {cell('School', spell.school, null)}
          {cell('Range', spell.range, null)}
        </div>
        <SpellTargetSelector casterKey={index} spellIdx={spellIdx} autoTargets={autoTargets} />
        <div className="vi-spell-exec-btn-row">
          <button className="vi-spell-exec-btn vi-spell-exec-single" onClick={onCastActive}>⚡ Cast On Active Monster</button>
          <button className="vi-spell-exec-btn vi-spell-exec-all" onClick={() => castSpell(index, spellIdx, targets)}>💥 Cast On All Active Monsters</button>
        </div>
        <div className="vi-quick-actions">
          <button className="vi-quick-btn vi-cast-end" onClick={onCastAndEndTurn}>⚡ CAST & END TURN<small>Finish current turn</small></button>
          <button className="vi-quick-btn vi-cast-next" onClick={onCastAndNextRound}>⏭ CAST & NEXT ROUND<small>Advance full combat</small></button>
        </div>
      </div>
    </div>
  );
}

export default SpellExec;
